/**
 * Handle gallery image click.
 *
 * @param {Event} event Event
 */
const handleClick = (event) => {
  const gallery = event?.target?.closest('.wp-block-gallery');

  if (!gallery) {
    return;
  }

  const lightbox = document.querySelector('.wp-lightbox-overlay');

  if (!lightbox) {
    return;
  }

  if (
    lightbox?.classList?.contains('active') &&
    (!lightbox?.querySelector('.rothschild-site-config__gallery-previous') ||
      !lightbox?.querySelector('.rothschild-site-config__gallery-next'))
  ) {
    removeGalleryLightboxControls();
    insertGalleryLightboxControls(gallery);
    return;
  }

  const observer = new MutationObserver((mutationList) => {
    const mutation = mutationList?.[0];

    if (!mutation || mutation?.type !== 'attributes' || mutation?.attributeName !== 'class') {
      observer.disconnect();
      return;
    }

    const isActive = mutation?.target?.classList?.contains('active');

    if (!isActive) {
      observer.disconnect();
      return;
    }

    removeGalleryLightboxControls();
    insertGalleryLightboxControls(gallery);
    observer.disconnect();
  });

  observer.observe(lightbox, {
    attributes: true,
    attributeFilter: ['class'],
  });
};

/**
 * Get the currently active image.
 *
 * @param {HTMLElement} gallery Current gallery scope.
 *
 * @return {undefined|HTMLElement}
 */
const getCurrentImage = (gallery) => {
  const selector = document.querySelector(
    '.wp-lightbox-overlay.active .wp-block-image img',
  )?.className;

  if (!selector) {
    return undefined;
  }

  return gallery.querySelector(`figure:has(img.${selector})`);
};

/**
 * Get target image for the next action.
 *
 * @param {string}      label        Action type.
 * @param {HTMLElement} initialImage Initial image.
 *
 * @return {undefined|HTMLElement}
 */
const getTargetImage = (label, initialImage) => {
  switch (label?.toLowerCase()) {
    case 'next':
      return initialImage?.nextElementSibling?.classList?.contains('wp-block-image')
        ? initialImage?.nextElementSibling
        : undefined;
    case 'previous':
      return initialImage?.previousElementSibling?.classList?.contains('wp-block-image')
        ? initialImage?.previousElementSibling
        : undefined;
    default:
      return undefined;
  }
};

/**
 * Generate button.
 *
 * @param {string}      label        Action label.
 * @param {HTMLElement} initialImage Initial image.
 * @param {HTMLElement} gallery      Current gallery scope.
 *
 * @return {HTMLElement}
 */
const buttonFactory = (label, initialImage, gallery) => {
  const button = document.createElement('button');

  button.textContent = label;
  button.setAttribute('type', 'button');
  button.setAttribute('aria-label', `${label} image`);
  button.classList.add(
    'rothschild-site-config__gallery-controls',
    `rothschild-site-config__gallery-${label?.toLowerCase()}`,
  );

  button.addEventListener('click', (event) => {
    event?.stopPropagation();
    navigateGallery(label?.toLowerCase(), gallery);
  });

  const targetImage = getTargetImage(label, initialImage);

  if (!targetImage) {
    button.setAttribute('aria-disabled', true);
    return button;
  }

  return button;
};

/**
 * Insert new gallery controls into the lightbox.
 *
 * @param {HTMLElement} gallery Current gallery scope.
 */
const insertGalleryLightboxControls = (gallery) => {
  const initialImage = getCurrentImage(gallery);

  if (!initialImage) {
    return;
  }

  const lightbox = document.querySelector('.wp-lightbox-overlay');

  lightbox.insertAdjacentElement('beforeend', buttonFactory('Previous', initialImage, gallery));
  lightbox.insertAdjacentElement('beforeend', buttonFactory('Next', initialImage, gallery));
};

/**
 * Control lightbox image switching.
 *
 * @param {string}      action  Action.
 * @param {HTMLElement} gallery Current gallery scope.
 */
const navigateGallery = (action, gallery) => {
  const currentImage = getCurrentImage(gallery);
  const targetImage = getTargetImage(action, currentImage);

  if (!targetImage) {
    return;
  }

  targetImage?.firstElementChild?.click();

  const previousImage = getTargetImage('previous', targetImage);
  const previousButton = document?.querySelector('.rothschild-site-config__gallery-previous');

  if (previousImage) {
    previousButton.removeAttribute('aria-disabled');
  } else {
    previousButton.setAttribute('aria-disabled', true);
  }

  const nextImage = getTargetImage('next', targetImage);
  const nextButton = document?.querySelector('.rothschild-site-config__gallery-next');

  if (nextImage) {
    nextButton.removeAttribute('aria-disabled');
  } else {
    nextButton.setAttribute('aria-disabled', true);
  }
};

/**
 * Remove all lightbox custom control elements.
 */
const removeGalleryLightboxControls = () => {
  const lightboxGalleryControls = document?.querySelectorAll(
    '.rothschild-site-config__gallery-controls',
  );

  lightboxGalleryControls?.forEach((lightboxGalleryControlItem) => {
    lightboxGalleryControlItem.remove();
  });
};

/**
 * Clear up relevant lightbox customisations when not active.
 *
 * @param {Array} mutationList MutationObserver mutation list.
 */
const lightboxClearUp = (mutationList) => {
  const mutation = mutationList?.[0];

  if (!mutation || mutation?.type !== 'attributes' || mutation?.attributeName !== 'class') {
    return;
  }

  const isActive = mutation?.target?.classList?.contains('active');

  if (isActive) {
    return;
  }

  removeGalleryLightboxControls();
};

/**
 * Handle tab navigation for gallery controls.
 *
 * @param {Event} event Event
 */
const handleKeyDown = (event) => {
  const lightbox = document.querySelector('.wp-lightbox-overlay');

  if (
    event?.key !== 'Tab' ||
    !lightbox?.querySelector('.rothschild-site-config__gallery-previous') ||
    !lightbox?.querySelector('.rothschild-site-config__gallery-next')
  ) {
    return;
  }

  event?.stopImmediatePropagation();
  const lightboxButtons = lightbox.querySelectorAll('button');
  const firstLightboxButton = lightboxButtons?.[0];
  const lastLightboxButton = lightboxButtons[lightboxButtons?.length - 1];

  if (event?.shiftKey && lightbox.ownerDocument.activeElement === firstLightboxButton) {
    event.preventDefault();
    lastLightboxButton?.focus();
  } else if (!event?.shiftKey && lightbox.ownerDocument.activeElement === lastLightboxButton) {
    event.preventDefault();
    firstLightboxButton?.focus();
  }
};

(() => {
  const galleries = document.querySelectorAll('.wp-site-blocks .wp-block-gallery');
  const lightbox = document.querySelector('.wp-lightbox-overlay');

  if (galleries?.length === 0 || !lightbox) {
    return;
  }

  galleries?.forEach((gallery) => {
    const galleryLightboxImages = gallery?.querySelectorAll('.wp-lightbox-container');

    if (galleryLightboxImages?.length <= 1) {
      return;
    }

    galleryLightboxImages.forEach((galleryLightboxImage) => {
      galleryLightboxImage.addEventListener('click', handleClick);
    });
  });

  const observer = new MutationObserver(lightboxClearUp);
  observer.observe(lightbox, { attributes: true, attributeFilter: ['class'] });
  lightbox.addEventListener('keydown', handleKeyDown);
})();
